import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaSearch, 
  FaFilter, 
  FaDownload, 
  FaEye, 
  FaTrash, 
  FaEdit,
  FaCheck,
  FaTimes,
  FaClock,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFileAlt,
  FaGraduationCap,
  FaUniversity,
  FaCalendarAlt,
  FaUserGraduate,
  FaUsers,
  FaUserTie,
  FaComments,
  FaStar,
  FaArrowLeft
} from 'react-icons/fa';
import { UniversityApplicantService } from '../../services/UniversityApplicantService';
import { UniversityOpportunityService } from '../../services/UniversityOpportunityService';

const UniversityReviewApplicationsPage = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    name: '',
    email: '',
    country: ''
  });
  const [pendingFilters, setPendingFilters] = useState({
    status: '',
    name: '',
    email: '',
    country: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    offset: 0,
    limit: 30
  });
  const [applicantsLoading, setApplicantsLoading] = useState(false);
  const [opportunityLoading, setOpportunityLoading] = useState(true);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Load opportunity and applicants when component mounts
  useEffect(() => {
    if (opportunityId) {
      loadOpportunity();
      loadApplicants();
      loadStats();
    }
  }, [opportunityId, filters, pagination.offset]);

  const loadOpportunity = async () => {
    try {
      setOpportunityLoading(true);
      const response = await UniversityOpportunityService.getOpportunityById(opportunityId);
      if (response) {
        setOpportunity(response);
      }
    } catch (error) {
      console.error('Failed to load opportunity:', error);
    } finally {
      setOpportunityLoading(false);
    }
  };

  const loadApplicants = async () => {
    if (!opportunityId) return;

    try {
      setApplicantsLoading(true);
      const params = {
        opportunity_id: opportunityId,
        limit: pagination.limit,
        offset: pagination.offset,
        ...filters
      };
      
      const response = await UniversityApplicantService.getApplicants(params);
      
      // Check if response is valid and has data
      if (response && response.data && Array.isArray(response.data)) {
        setApplicants(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0
        }));
      } else if (response && Array.isArray(response)) {
        // Handle direct array response
        setApplicants(response);
        setPagination(prev => ({
          ...prev,
          total: response.length
        }));
      } else {
        setApplicants([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } catch (error) {
      console.error('Failed to load applicants:', error);
      // Clear the applicants list when there's an error
      setApplicants([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
    } finally {
      setApplicantsLoading(false);
    }
  };

  const loadStats = async () => {
    if (!opportunityId) return;

    try {
      const response = await UniversityApplicantService.getApplicantStats({
        opportunity_id: opportunityId
      });
      if (response) {
        setStats(response);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleStatusUpdate = async (applicantId, newStatus) => {
    try {
      await UniversityApplicantService.updateApplicantStatus(applicantId, {
        status: newStatus
      });
      // Force refresh by resetting pagination and reloading data
      setPagination(prev => ({ ...prev, offset: 0 }));
      await loadApplicants();
      await loadStats();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleCreateComment = async (applicantId, comment) => {
    try {
      await UniversityApplicantService.createComment(applicantId, comment);
      loadApplicants();
    } catch (error) {
      console.error('Failed to create comment:', error);
    }
  };

  const handleDeleteComment = async (applicantId, commentId) => {
    try {
      await UniversityApplicantService.deleteComment(applicantId, commentId);
      loadApplicants();
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleDownloadResume = async (applicantId) => {
    try {
      const response = await UniversityApplicantService.downloadResume(applicantId);
      if (response && response.download_url) {
        window.open(response.download_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to download resume:', error);
    }
  };

  const handleDeleteApplicant = async (applicantId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      await UniversityApplicantService.deleteApplicant(applicantId);
      loadApplicants();
      loadStats();
    } catch (error) {
      console.error('Failed to delete applicant:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setPendingFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setFilters({ ...pendingFilters });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleReset = () => {
    const resetFilters = { status: '', name: '', email: '', country: '' };
    setPendingFilters(resetFilters);
    setFilters(resetFilters);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const loadComments = async (applicantId) => {
    try {
      setLoadingComments(true);
      const response = await UniversityApplicantService.getComments(applicantId);
      if (response && Array.isArray(response)) {
        setComments(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setComments(response.data);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'reviewed': return 'bg-yellow-100 text-yellow-800';
      case 'shortlisted': return 'bg-purple-100 text-purple-800';
      case 'offered': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <FaClock className="w-4 h-4" />;
      case 'reviewed': return <FaEye className="w-4 h-4" />;
      case 'shortlisted': return <FaStar className="w-4 h-4" />;
      case 'offered': return <FaCheck className="w-4 h-4" />;
      case 'rejected': return <FaTimes className="w-4 h-4" />;
      default: return <FaClock className="w-4 h-4" />;
    }
  };

  if (opportunityLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FFD6D4] border-t-[#DB4F40]"></div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="text-center py-8">
        <p className="text-[#1B222A]/70">Opportunity not found</p>
        <button
          onClick={() => navigate('/university/dashboard')}
          className="mt-4 px-6 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/university/dashboard', { state: { section: 'opportunities' } })}
            className="flex items-center px-4 py-2 bg-white text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors mb-4"
            title="Back to Opportunities"
          >
            <FaArrowLeft className="w-4 h-4 mr-2" />
            Back to Opportunities
          </button>
          
          <div className="bg-white rounded-2xl shadow-lg border-2 border-[#FFD6D4] p-6">
            <h1 className="text-3xl font-bold text-[#DB4F40] mb-2">Review Applications</h1>
            <p className="text-[#1B222A]/70 text-lg">{opportunity.title}</p>
            <div className="flex items-center gap-4 text-[#1B222A]/60 mt-1">
              <span className="flex items-center gap-1">
                <span className="relative group cursor-help">
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    University
                  </span>
                  <FaUniversity className="w-4 h-4" />
                </span>
                <span>{opportunity.university}</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="relative group cursor-help">
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    Department
                  </span>
                  <FaGraduationCap className="w-4 h-4" />
                </span>
                <span>{opportunity.department}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl shadow-lg border-2 border-[#FFD6D4] p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaUser className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-[#1B222A]/70">Total Applications</p>
                  <p className="text-xl font-bold text-[#1B222A]">{stats.total_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-[#FFD6D4] p-4">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FaEye className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-[#1B222A]/70">Reviewed</p>
                  <p className="text-xl font-bold text-[#1B222A]">{stats.status_counts?.reviewed || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-[#FFD6D4] p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaClock className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-[#1B222A]/70">New</p>
                  <p className="text-xl font-bold text-[#1B222A]">{stats.status_counts?.new || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-lg border-2 border-[#FFD6D4] p-6">
            <h3 className="text-lg font-semibold text-[#DB4F40] mb-4">Filter Applications</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-[#1B222A] mb-2">Status</label>
                <select
                  value={pendingFilters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent"
                >
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="offered">Offered</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1B222A] mb-2">Name</label>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={pendingFilters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1B222A] mb-2">Email</label>
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={pendingFilters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1B222A] mb-2">Country</label>
                <input
                  type="text"
                  placeholder="Search by country..."
                  value={pendingFilters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end mt-2">
              <button
                onClick={handleSearch}
                className="px-6 py-2 rounded-lg bg-[#DB4F40] text-white font-semibold hover:bg-[#C64537] transition-all shadow-sm mr-2"
                type="button"
              >
                Search
              </button>
              <button
                onClick={handleReset}
                className="px-6 py-2 rounded-lg bg-[#DB4F40] text-white font-semibold hover:bg-[#C64537] transition-all shadow-sm"
                type="button"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Applications List */}
          <div className="bg-white rounded-2xl shadow-lg border-2 border-[#FFD6D4]">
            <div className="p-6 border-b border-[#FFD6D4]">
              <h3 className="text-lg font-semibold text-[#DB4F40]">
                Applications ({pagination.total})
              </h3>
            </div>
            
            {applicantsLoading ? (
              <div className="p-6 text-center">
                <div className="w-8 h-8 border-4 border-[#DB4F40] border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="mt-2 text-[#1B222A]/70">Loading applications...</p>
              </div>
            ) : applicants.length === 0 ? (
              <div className="p-6 text-center">
                <FaUser className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-[#1B222A]/70">No applications found</p>
              </div>
            ) : (
              <div className="divide-y divide-[#FFD6D4]">
                {applicants.map((applicant) => (
                  <ApplicantCard
                    key={applicant.id}
                    applicant={applicant}
                    onStatusUpdate={handleStatusUpdate}
                    onCreateComment={handleCreateComment}
                    onDeleteComment={handleDeleteComment}
                    onDownloadResume={handleDownloadResume}
                    onDelete={handleDeleteApplicant}
                    onShowComments={() => {
                      setSelectedApplicant(applicant);
                      setShowCommentsModal(true);
                      loadComments(applicant.id);
                    }}
                    onAddComment={() => {
                      setSelectedApplicant(applicant);
                      setShowCommentModal(true);
                    }}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="p-6 border-t border-[#FFD6D4]">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#1B222A]/70">
                    Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} applications
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                      disabled={pagination.offset === 0}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                      disabled={pagination.offset + pagination.limit >= pagination.total}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Comment Modal */}
        {showCommentModal && selectedApplicant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-[#1B222A] mb-4">Add Comment for {selectedApplicant.name}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1B222A] mb-2">Comment</label>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={4}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent"
                    placeholder="Add your comment about this application..."
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCommentModal(false);
                    setNewComment('');
                    setSelectedApplicant(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-[#1B222A] hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newComment.trim()) {
                      handleCreateComment(selectedApplicant.id, newComment.trim());
                      setNewComment('');
                      setShowCommentModal(false);
                      setSelectedApplicant(null);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors"
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Comments Modal */}
        {showCommentsModal && selectedApplicant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#1B222A]">Comments for {selectedApplicant.name}</h3>
                <button
                  onClick={() => {
                    setShowCommentsModal(false);
                    setSelectedApplicant(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
              
              {loadingComments ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DB4F40] mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading comments...</p>
                </div>
              ) : comments.length > 0 ? (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-[#1B222A]">{comment.reviewer_name || 'Unknown Reviewer'}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteComment(selectedApplicant.id, comment.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          title="Delete comment"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-[#1B222A] whitespace-pre-wrap">{comment.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No comments yet.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Applicant Card Component (same as before)
const ApplicantCard = ({ applicant, onStatusUpdate, onCreateComment, onDeleteComment, onDownloadResume, onDelete, onShowComments, onAddComment }) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState(applicant.status);

  const handleStatusSubmit = () => {
    onStatusUpdate(applicant.id, newStatus);
    setShowStatusModal(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'reviewed': return 'bg-yellow-100 text-yellow-800';
      case 'shortlisted': return 'bg-purple-100 text-purple-800';
      case 'offered': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <FaClock className="w-4 h-4" />;
      case 'reviewed': return <FaEye className="w-4 h-4" />;
      case 'shortlisted': return <FaStar className="w-4 h-4" />;
      case 'offered': return <FaCheck className="w-4 h-4" />;
      case 'rejected': return <FaTimes className="w-4 h-4" />;
      default: return <FaClock className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <h4 className="text-lg font-semibold text-[#1B222A]">{applicant.name}</h4>
            <span 
              className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(applicant.status)}`}
              title={`Application Status: ${applicant.status.charAt(0).toUpperCase() + applicant.status.slice(1)}`}
            >
              {getStatusIcon(applicant.status)}
              <span className="capitalize">{applicant.status}</span>
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <span className="relative group cursor-help">
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    Email Address
                  </span>
                  <FaEnvelope className="w-4 h-4 text-gray-500" />
                </span>
                <span className="text-sm text-[#1B222A]">{applicant.email}</span>
              </div>
              {applicant.contact_mobile && (
                <div className="flex items-center space-x-2">
                  <span className="relative group cursor-help">
                    <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      Phone Number
                    </span>
                    <FaPhone className="w-4 h-4 text-gray-500" />
                  </span>
                  <span className="text-sm text-[#1B222A]">{applicant.contact_mobile}</span>
                </div>
              )}
              {applicant.country && (
                <div className="flex items-center space-x-2">
                  <span className="relative group cursor-help">
                    <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      Country
                    </span>
                    <FaMapMarkerAlt className="w-4 h-4 text-gray-500" />
                  </span>
                  <span className="text-sm text-[#1B222A]">{applicant.country}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <span className="relative group cursor-help">
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    Application Date
                  </span>
                  <FaCalendarAlt className="w-4 h-4 text-gray-500" />
                </span>
                <span className="text-sm text-[#1B222A]">
                  Applied: {new Date(applicant.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
          {/* Applicant Comments Section */}
          {applicant.comments && (
            <div className="mt-2 p-3 bg-[#FFEEED] border border-[#FFD6D4] rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <FaComments className="w-4 h-4 text-[#DB4F40]" />
                <span className="font-semibold text-[#DB4F40]">Applicant Comments:</span>
              </div>
              <div className="text-[#1B222A] whitespace-pre-line text-sm">{applicant.comments}</div>
            </div>
          )}
        </div>

        <div className="flex space-x-2 ml-4">
          {applicant.resume_url && (
            <button
              onClick={() => onDownloadResume(applicant.id)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors relative group"
              title="Download Resume"
            >
              <FaDownload className="w-4 h-4" />
              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                Download Resume
              </span>
            </button>
          )}
          <button
            onClick={onShowComments}
            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors relative group"
            title="View Comments"
          >
            <FaComments className="w-4 h-4" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
              View Comments
            </span>
          </button>
          <button
            onClick={onAddComment}
            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors relative group"
            title="Add Comment"
          >
            <FaEdit className="w-4 h-4" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
              Add Comment
            </span>
          </button>
          <button
            onClick={() => setShowStatusModal(true)}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors relative group"
            title="Update Status"
          >
            <FaCheck className="w-4 h-4" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
              Update Status
            </span>
          </button>
          <button
            onClick={() => onDelete(applicant.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors relative group"
            title="Delete Application"
          >
            <FaTrash className="w-4 h-4" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
              Delete Application
            </span>
          </button>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-[#1B222A] mb-4">Update Application Status</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1B222A] mb-2">Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent"
                >
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="offered">Offered</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-[#1B222A] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusSubmit}
                className="flex-1 px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversityReviewApplicationsPage;
